import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import api, { formatApiError } from "../lib/api";
import { Logo } from "../components/Logo";
import { Input } from "../components/ui/input";

const inputCls =
  "rounded-none bg-obsidian border-olive-deep text-foreground placeholder:text-coyote/60 focus-visible:ring-1 focus-visible:ring-khaki focus-visible:border-khaki";

function InvoiceLookup() {
  const navigate = useNavigate();
  const [number, setNumber] = useState("");

  const go = (e) => {
    e.preventDefault();
    const clean = number.trim().toUpperCase();
    if (!clean) return;
    navigate(`/pay/${encodeURIComponent(clean)}`);
  };

  return (
    <div className="w-full max-w-sm border border-olive-deep bg-obsidian p-8 sm:p-10" data-testid="invoice-lookup">
      <p className="overline-tactical text-coyote mb-4">/// PAY AN INVOICE</p>
      <h1 className="font-display uppercase text-3xl text-foreground mb-3">Enter Invoice Number</h1>
      <p className="text-xs text-muted-foreground leading-relaxed mb-8">
        Your invoice number is on the invoice we sent you (format: INV-0001).
      </p>
      <form onSubmit={go} className="space-y-5">
        <div>
          <label className="block text-[10px] tracking-[0.3em] text-coyote mb-2">INVOICE NUMBER</label>
          <Input
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            placeholder="INV-0001"
            className={inputCls}
            data-testid="invoice-number-input"
          />
        </div>
        <button
          type="submit"
          data-testid="invoice-lookup-button"
          className="dogtag w-full bg-olive text-white font-bold text-sm tracking-[0.25em] py-3.5 hover:bg-khaki hover:text-obsidian transition-colors duration-300"
        >
          [ FIND INVOICE ]
        </button>
      </form>
    </div>
  );
}

export default function PayInvoicePage() {
  const { invoiceId } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [config, setConfig] = useState(null);
  const [error, setError] = useState("");
  const [paid, setPaid] = useState(false);

  useEffect(() => {
    if (!invoiceId) return;
    setError("");
    setInvoice(null);
    api.get(`/invoices/${encodeURIComponent(invoiceId)}`)
      .then((res) => setInvoice(res.data))
      .catch((err) => setError(formatApiError(err)));
    api.get("/paypal/config").then((res) => setConfig(res.data)).catch(() => setConfig({ configured: false }));
  }, [invoiceId]);

  return (
    <div className="min-h-screen bg-obsidian blueprint-grid flex flex-col" data-testid="pay-invoice-page">
      <header className="border-b border-olive-deep">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <Logo />
          <a href="/" className="text-[10px] tracking-[0.25em] text-coyote hover:text-khaki transition-colors duration-300" data-testid="pay-back-link">
            &lt;&lt; BACK TO SITE
          </a>
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center px-4 py-16">
        {!invoiceId ? (
          <InvoiceLookup />
        ) : error ? (
          <div className="w-full max-w-sm border border-alert/50 bg-obsidian p-8 text-center" data-testid="invoice-error">
            <p className="text-alert text-sm tracking-[0.2em]">!! {error}</p>
            <p className="mt-4 text-xs text-coyote">Check the invoice number and try again, or call 283-237-0359.</p>
          </div>
        ) : !invoice ? (
          <span className="text-xs tracking-[0.3em] text-coyote">LOCATING INVOICE<span className="blink text-alert">_</span></span>
        ) : (
          <div className="w-full max-w-md border border-olive-deep bg-obsidian p-8 sm:p-10" data-testid="invoice-panel">
            <p className="overline-tactical text-coyote mb-4">/// OPERATION FIX-IT INVOICE</p>
            <div className="flex items-end justify-between border-b border-olive-deep pb-5 mb-6">
              <div>
                <span className="text-[10px] tracking-[0.3em] text-coyote block">INVOICE</span>
                <span className="font-display text-3xl text-foreground" data-testid="invoice-number">{invoice.invoice_number}</span>
              </div>
              <span
                className={`text-[10px] tracking-[0.25em] font-bold border px-3 py-1.5 ${
                  invoice.status === "PAID" || paid ? "text-olive border-olive bg-olive/20" : "text-alert border-alert/50 bg-alert/10"
                }`}
                data-testid="invoice-status"
              >
                {paid ? "PAID" : invoice.status}
              </span>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-coyote text-xs tracking-[0.2em]">BILLED TO</span><span className="text-foreground font-bold">{invoice.customer_name}</span></div>
              <div className="flex justify-between"><span className="text-coyote text-xs tracking-[0.2em]">DATE</span><span className="text-muted-foreground">{invoice.created_at ? format(new Date(invoice.created_at), "MM/dd/yyyy") : "-"}</span></div>
              <div className="pt-2 border-t border-olive-deep">
                <span className="text-coyote text-xs tracking-[0.2em] block mb-1">WORK PERFORMED</span>
                <p className="text-muted-foreground leading-relaxed">{invoice.description}</p>
              </div>
              <div className="flex justify-between items-center pt-4 border-t border-olive-deep">
                <span className="text-coyote text-xs tracking-[0.2em]">TOTAL DUE</span>
                <span className="font-display text-4xl text-khaki" data-testid="invoice-amount">${invoice.amount.toFixed(2)}</span>
              </div>
            </div>

            {invoice.status === "PAID" || paid ? (
              <div className="mt-8 border border-olive bg-olive/10 p-5 text-center" data-testid="invoice-paid-panel">
                <p className="font-bold tracking-[0.2em] text-olive text-sm">PAYMENT RECEIVED — MISSION COMPLETE</p>
                <p className="mt-2 text-xs text-muted-foreground">A receipt has been emailed to you. Thank you for choosing OPERATION FIX-IT.</p>
              </div>
            ) : config && config.configured ? (
              <div className="mt-8" data-testid="paypal-buttons">
                <PayPalScriptProvider options={{ "client-id": config.client_id, currency: "USD" }}>
                  <PayPalButtons
                    style={{ layout: "vertical", shape: "rect", color: "gold" }}
                    createOrder={async () => {
                      const res = await api.post(`/invoices/${invoice.id}/paypal-order`);
                      return res.data.order_id;
                    }}
                    onApprove={async (data) => {
                      try {
                        await api.post(`/invoices/${invoice.id}/capture/${data.orderID}`);
                        setPaid(true);
                        toast.success("PAYMENT RECEIVED", { description: `${invoice.invoice_number} marked paid.` });
                      } catch (err) {
                        toast.error("CAPTURE FAILED", { description: formatApiError(err) });
                      }
                    }}
                    onError={() => toast.error("PAYPAL ERROR", { description: "The payment could not be completed. Try again or call 283-237-0359." })}
                  />
                </PayPalScriptProvider>
              </div>
            ) : (
              <div className="mt-8 border border-olive-deep p-5 text-center" data-testid="paypal-pending">
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Online payment is being activated for this invoice. To pay now, call
                  <span className="text-khaki font-bold"> 283-237-0359</span> (Mon–Fri 9AM–5PM).
                </p>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
